import React, { lazy, Suspense } from "react";

import backgroundImg from "../images/BG-hands.webp";
import NGOsFunction from "../NGOsData";
import SearchBar from "../components/SearchBar";
import { CustomLink } from "../components/CustomLink";

import {stringToPath} from "../lib/stringToPath"

const LazyCard = lazy(() => import("../components/Card"));
const OrgSizeCard = lazy(() => import("../components/OrgSizeCard"));
const LazyFooter = lazy(() => import("../components/Footer"));
const LazyHeader = lazy(() => import("../components/Header"));

const OrgSizeArray = [
  {
    title: "Top 200 charities",
    key: "Top_200_charities"
  },
  {
    title: "Top 201-400 charities",
    key: "Top_201-400_charities"
  },
  {
    title: "Top 401-600 charities",
    key: "Top_401-600_charities"
  },
  {
    title: "Top 601-800 charities",
    key: "Top_601-800_charities"
  },
  {
    title: "Top 801-1000 charities",
    key: "Top_801-1000_charities"
  },
  {
    title: "Very large charities",
    key: "Very_large_charities"
  },
  {
    title: "Large charities",
    key: "Large_charities"
  },
  {
    title: "Medium Charities",
    key: "Medium_Charities"
  },

  {
    title: "Small Charities",
    key: "Small_Charities"
  },
  {
    title: "Very Small Charities",
    key: "Very_Small_Charities"
  }
];

const OrgCategories = [
  {
    title: "Arts, Culture, and Humanities",
    key: "Arts_Culture_and_Humanities",
    subCategories: [{ title: "Arts", key: "arts" }]
  },
  {
    title: "Education",
    key: "Education",
    subCategories: [{ title: "Education", key: "education" }]
  },
  {
    title: "Environment & Animals",
    key: "Environment_Animals",
    subCategories: [{ title: "Environment", key: "environment" }, { title: "Animals", key: "Animals" }]
  },
  {
    title: "Health",
    key: "Health",
    subCategories: [
      { title: "Health", key: "health" },
      { title: "Mental Health", key: "mental_health" },
      { title: "Disease", key: "disease" },
      { title: "Medical Research", key: "medical_research" }
    ]
  },
  {
    title: "Human Services",
    key: "Human_Services",
    subCategories: [
      { title: "Crime", key: "crime" },
      { title: "Employment", key: "employment" },
      { title: "Food", key: "food" },
      { title: "Housing", key: "housing" },
      { title: "Public Safety", key: "public" },
      { title: "Recreation", key: "recreation" },
      { title: "Youth Development", key: "youth" },
      { title: "Human Services", key: "human_services" }
    ]
  },
  {
    title: "International, Foreign Affairs",
    key: "International_Foreign_Affairs",
    subCategories: [{ title: "International", key: "international" }]
  },
  {
    title: "Public, Societal Benefit",
    key: "Public_Societal_Benefit",
    subCategories: [
      { title: "Civil Rights", key: "civil_rights" },
      { title: "Community Improvement", key: "community_improvement" },
      { title: "Philanthropy", key: "philanthropy" },
      { title: "Science Research", key: "science_research" },
      { title: "Social Research", key: "social_research" },
      { title: "Public Benefit", key: "public_benefit" }
    ]
  },
  {
    title: "Religion Related",
    key: "Religion_Related",
    subCategories: [{ title: "Religion Related", key: "religion_related" }]
  },
  {
    title: "Mutual/Membership Benefit ",
    key: "Mutual_Membership_Benefit",
    subCategories: [{ title: "Mutual Benefit", key: "mutual_benefit" }]
  },
  {
    title: "Unknown, Unclassified",
    key: "Unknown_Unclassified",
    subCategories: [{ title: "Unknown", key: "unknown" }]
  },
  {
    title: "No Designated category",
    key: "No_designated_category",
    subCategories: [{ title: "Not Designated", key: "not_designated" }]
  }
];

const formatNumber = (number, upDown) => {
  //first remove dollar and commas from string and convert to number
  let num = Number(number.replace(/[$,]/g, ""));
  num = upDown ? Math.ceil(num / 1000) * 1000 : Math.floor(num / 1000) * 1000;
  num = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return num;
};

const compareRevenue = (a, b) => {
  // Remove the dollar sign and commas from the revenue strings
  const revenueA = Number(a.crtorev.replace(/[$,]/g, ""));
  const revenueB = Number(b.crtorev.replace(/[$,]/g, ""));
  // Compare the revenue values in descending order
  return revenueB - revenueA;
};

const IndexPage = () => {
  const NGOsJson = NGOsFunction();
  let sortedCards = NGOsJson.sort(compareRevenue);
  let charitySize = 0;

  if (sortedCards.length > 1000) {
    sortedCards = sortedCards.slice(1000);
    charitySize = sortedCards.length / 5;
  }

  return (
    <Suspense fallback={<div />}>
      <div>
        <LazyHeader />
        <main>
          <div
            className="w-full px-7 py-10  lg:pt-[60px] 2xl:px-44"
            style={{
              backgroundImage: `url(${backgroundImg})`,
              backgroundSize: "cover"
            }}
            loading="lazy"
          >
            <h1 className="mb-10 text-center text-2xl font-bold lg:text-7xl">
              Find a charity that aligns with your passions and values
            </h1>
            <SearchBar />
            <div className="text-center">
              <h2 className="my-4 text-center text-xl font-semibold lg:text-4xl">
                Find Your Charity by Category
              </h2>
              <h3 className="font-semibold flex  justify-center text-lg lg:text-xl">
                Each charity is designated a category by the IRS.
              </h3>
              <div className="flex justify-center text-sm my-2 mx-10">
                <p className="lg:max-w-[500px]">Note: Give Freely supports more charities than are listed in this directory.
                  Give Freely is a browser extension that donates to charity when you shop at a partner store.
                  You can select any of the charities in this directory along with 1+ million other charities to support through Give Freely.
                  <a href="https://givefreely.com"
                  target="_blank" className="text-[#0d82ee]"> Learn more about how to use Give Freely to support your favorite charity.</a></p>
              </div>
            </div>

            <div className="mt-8 grid gap-7 md:grid-cols-2 lg:mt-12 lg:grid-cols-3">
              {OrgCategories.map((OrgCategory) => (
                <LazyCard data={OrgCategory} value={OrgCategory.name} key={OrgCategory.key} />
              ))}
            </div>
          </div>

          <div className="my-2 rounded-lg bg-white p-4 lg:px-44">
            <div>
              <h2 className="py-4  text-center text-xl  font-semibold lg:text-4xl">
                Find Your Charity by Size
              </h2>
              <h3 className="text-semibold mb-4  flex justify-center text-center text-sm lg:text-xl">
                A charity’s size is sorted by revenue, which is from their
                most recently reported tax forms.
              </h3>

              <div className="mt-[30px] grid gap-7 px-2 md:grid-cols-2 md:px-0 lg:mt-[60px] lg:grid-cols-3">
                {OrgSizeArray?.slice(0, 5).map((OrgSize) => (
                  <CustomLink
                    ariaLabel={OrgSize.title}
                    href={`/${stringToPath(OrgSize.title)}`}
                    key={`top-by-revenue-${OrgSize.key}`}
                  >
                    <OrgSizeCard data={OrgSize} />
                  </CustomLink>
                )
                )}
              </div>
              <br />
              <hr />
              <br />
              <h2 className="py-4 text-center text-lg font-semibold lg:text-4xl">
                Index for Charities Past the Top 1000
              </h2>
              <div className="mt-[30px] grid gap-7 px-2 md:grid-cols-2 md:px-0 lg:mt-[60px] lg:grid-cols-3">
                {
                      //find the start and end revenue of the sorted card that fall into charity size based on index of orgsizearray slice
                      OrgSizeArray?.slice(5).map((OrgSize, index) => {
                        const newind = index + 1;
                        const tmp = sortedCards.slice(
                          (newind - 1) * charitySize,
                          newind * charitySize
                        );

                        if (tmp.length > 0) {
                          const txt = `$${formatNumber(
                            tmp[tmp.length - 1].crtorev,
                            false
                          )} - $${formatNumber(tmp[0].crtorev, true)}`;
                          return (
                            <CustomLink
                              aria-label={OrgSize.title}
                              href={`/${stringToPath(OrgSize.title)}`}
                              key={`top-charity-${OrgSize.key}`}
                            >
                              <OrgSizeCard data={OrgSize} subtext={txt} showTip={
                                tmp[tmp.length - 1].crtorev.includes("-")
                               } />
                            </CustomLink>
                          );
                        }

                        return null;
                      })
                    }
              </div>
            </div>
          </div>
        </main>
        <LazyFooter />
      </div>
    </Suspense>
  );
};
export default IndexPage;

export const Head = () => 
<>
  <title>Charity Directory</title>
  <meta name="description" content="Search for a charity that aligns with your passions and values. Find your charity by category or size." />
</>
